import { faRemove } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { Button, CardV2 as Card, Chip, EmptyState, Text, Well } from '@feathr/components';
import { Glue } from '@feathr/extender/components';
import { useStore } from '@feathr/extender/state';
import { cssVar } from '@feathr/hooks';

import { getAgeDemographicTargetings } from '../AgeDemographics/AgeDemographics';
import { getInterestsTargetings } from '../Interests';

import * as styles from './SelectedFilters.css';

interface IProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

function SelectedFilterButton({
  targeting,
  removeTargeting,
  name,
}: {
  targeting: Targeting;
  removeTargeting: (targeting: Targeting) => void;
  name: string;
}): JSX.Element {
  function handleRemoveTargeting(): void {
    removeTargeting(targeting);
  }

  return (
    <Chip
      className={styles.chipFix}
      suffix={
        <Button onClick={handleRemoveTargeting} type={'naked'}>
          <FontAwesomeIcon className={'icon'} icon={faRemove} />
        </Button>
      }
      theme={'sky'}
    >
      {name}
    </Chip>
  );
}

function SelectedFiltersWell({
  targetings,
  label,
  children,
  glue = false,
}: {
  targetings: Targeting[];
  label: string;
  children: JSX.Element[];
  glue?: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <Well
        className={styles.selectedFilters}
        description={targetings.length > 1 && t('any of the selected')}
        label={label}
        layout={'vertical'}
      >
        {targetings.length > 0 ? (
          <Flex gap={cssVar('--spacing-2')} wrap={'wrap'}>
            {children}
          </Flex>
        ) : (
          <Text.DefaultRegular>{t('None selected')}</Text.DefaultRegular>
        )}
      </Well>
      {glue && <Glue suppressGap={true} t={t} />}
    </>
  );
}

function SelectedFilters({ targetings }: Readonly<IProps>): JSX.Element {
  const { Targetings } = useStore();
  const { t } = useTranslation();

  function removeTargeting(targeting: Targeting): void {
    if (!targeting.isEphemeral) {
      targeting.set({ is_archived: true });
    } else {
      targetings.remove(targeting);
      Targetings.remove(targeting.id);
    }
  }

  const geoTargetings = targetings.filter(
    (t) => !t.get('is_archived') && !!t.get('name') && t.get('kind') === 'geo',
  );
  const ageDemographicTargetings = getAgeDemographicTargetings(targetings);
  const interestsTargetings = getInterestsTargetings(targetings);

  const allTargetings = [...geoTargetings, ...ageDemographicTargetings, ...interestsTargetings];

  function renderSelectedFilters(targetings: Targeting[]): JSX.Element[] {
    return targetings.map((targeting): JSX.Element => {
      const name =
        targeting.get('kind') === 'age_demographic'
          ? t('{{ range }} years', { range: targeting.get('name') })
          : targeting.get('name');

      return (
        <SelectedFilterButton
          key={targeting.id}
          name={name}
          removeTargeting={removeTargeting}
          targeting={targeting}
        />
      );
    });
  }

  function handleClearAllFilters(): void {
    allTargetings.forEach((targeting) => {
      removeTargeting(targeting);
    });
  }

  return (
    <Card contentClassName={styles.root} isSticky={true} width={'full'}>
      <Card.Header title={t('Selected Filters')} />
      <Card.Content addVerticalGap={true}>
        {allTargetings.length === 0 ? (
          <EmptyState
            description={t('Get started by selecting some filters')}
            label={t('No filters selected')}
            theme={'slate'}
          />
        ) : (
          <>
            <SelectedFiltersWell glue={true} label={t('Locations')} targetings={geoTargetings}>
              {renderSelectedFilters(geoTargetings)}
            </SelectedFiltersWell>

            <SelectedFiltersWell
              glue={true}
              label={t('Age Demographics')}
              targetings={ageDemographicTargetings}
            >
              {renderSelectedFilters(ageDemographicTargetings)}
            </SelectedFiltersWell>

            <SelectedFiltersWell label={t('Interests')} targetings={interestsTargetings}>
              {renderSelectedFilters(interestsTargetings)}
            </SelectedFiltersWell>

            {allTargetings.length > 0 && (
              <Button onClick={handleClearAllFilters}>{t('Clear filters')}</Button>
            )}
          </>
        )}
      </Card.Content>
    </Card>
  );
}

export default observer(SelectedFilters);
